import React, { useState, useEffect, useCallback } from 'react';
import { Header } from '../components/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Camera, Upload, X, CheckCircle2, Loader2, Star, ArrowLeft } from 'lucide-react';
import { toast, Toaster } from 'sonner';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const StarRating = ({ rating, onRate }) => (
  <div className="flex gap-1">
    {[1, 2, 3, 4, 5].map((star) => (
      <button key={star} type="button" onClick={() => onRate(star)} className="transition-all duration-200 hover:scale-110">
        <Star className={`w-7 h-7 ${star <= rating ? 'text-amber-500 fill-amber-500' : 'text-zinc-700'}`} />
      </button>
    ))}
  </div>
);

const SubmitReviewPage = () => {
  const navigate = useNavigate();
  const [reviews, setReviews] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [previews, setPreviews] = useState([]);
  const [form, setForm] = useState({
    customer_name: '', description: '', rating: 0,
  });

  const fetchData = useCallback(async () => {
    // Fetch reviews and gallery independently so reviews show immediately
    axios.get(`${BACKEND_URL}/api/reviews`)
      .then(res => setReviews(res.data))
      .catch(() => {})
      .finally(() => setReviewsLoading(false));

    axios.get(`${BACKEND_URL}/api/gallery`)
      .then(res => setPhotos(res.data))
      .catch(() => {});
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (previews.length + files.length > 3) { toast.error('Maximum 3 photos allowed'); return; }
    files.forEach((file) => {
      if (file.size > 10 * 1024 * 1024) { toast.error(`${file.name} is over 10MB`); return; }
      const reader = new FileReader();
      reader.onloadend = () => setPreviews((prev) => [...prev, reader.result]);
      reader.readAsDataURL(file);
    });
  };

  const removePreview = (index) => setPreviews((prev) => prev.filter((_, i) => i !== index));

  const handleSubmit = async () => {
    if (!form.customer_name || !form.vehicle_year || !form.vehicle_make || !form.vehicle_model) {
      toast.error('Please fill all required fields');
      return;
    }
    setUploading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/reviews`, {
        name: form.customer_name,
        email: '',
        phone: '',
        service: `${form.vehicle_year} ${form.vehicle_make} ${form.vehicle_model}`,
        rating: form.rating,
        review: form.description,
      });
      setSubmitted(true);
      setForm({ customer_name: '', vehicle_year: '', vehicle_make: '', vehicle_model: '', description: '', rating: 0 });
      setPreviews([]);
      fetchData();
    } catch (err) {
      toast.error('Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const imgUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    if (url.startsWith('/api/')) return `${BACKEND_URL}${url}`;
    if (url.startsWith('/uploads/')) return `${BACKEND_URL}/api${url}`;
    return url;
  };

  // Build unified feed: reviews with matching photos
  const buildFeed = () => {
    const usedPhotoIds = new Set();
    const feed = [];
    reviews.forEach((rev) => {
      const matched = photos.filter((p) => p.customer_name === rev.name && !usedPhotoIds.has(p.id)).slice(0, 2);
      matched.forEach((p) => usedPhotoIds.add(p.id));
      feed.push({ type: 'review', review: rev, photos: matched });
    });
    const unmatched = photos.filter((p) => !usedPhotoIds.has(p.id));
    for (let i = 0; i < unmatched.length; i += 2) {
      feed.push({ type: 'gallery', photos: unmatched.slice(i, i + 2) });
    }
    return feed;
  };

  const feed = buildFeed();
  const hasContent = reviews.length > 0 || photos.length > 0;

  return (
    <div className="min-h-screen bg-black">
      <main className="pt-10 pb-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back arrow */}
          <button
            onClick={() => navigate('/')}
            data-testid="back-to-home-btn"
            style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              background: 'none', border: 'none', cursor: 'pointer',
              color: '#a1a1aa', fontSize: '14px', fontWeight: 600,
              marginBottom: '24px', padding: '0',
            }}
          >
            <ArrowLeft className="w-5 h-5" strokeWidth={2.5} />
            Back
          </button>
          {/* Upload Form */}
          {showUpload && (
            <div className="max-w-lg mx-auto mb-6" data-testid="upload-form">
              {submitted ? (
                <div className="bg-green-500/10 border border-green-500/30 rounded-2xl p-8 text-center">
                  <CheckCircle2 className="w-12 h-12 text-green-400 mx-auto mb-4" />
                  <h3 className="text-white text-xl font-semibold mb-2">Review Submitted!</h3>
                  <p className="text-zinc-400 text-sm">Your review is under review and will appear once approved.</p>
                  <Button onClick={() => { setSubmitted(false); setShowUpload(false); }} className="mt-6 rounded-full bg-zinc-800 hover:bg-zinc-700 text-white text-sm px-6">
                    Done
                  </Button>
                </div>
              ) : (
                <div className="bg-zinc-950 border border-zinc-800 rounded-2xl p-6 md:p-8 space-y-5">
                  <h3 className="text-white text-lg font-semibold text-center mb-2">Share Your Ride</h3>

                  {/* Name */}
                  <div>
                    <Label className="text-white text-sm mb-2 block">Your Name *</Label>
                    <Input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                      placeholder="John Doe" className="bg-black border-zinc-800 text-white" data-testid="gallery-input-name" />
                  </div>

                  {/* Rating */}
                  <div>
                    <Label className="text-white text-sm mb-2 block">Rating</Label>
                    <StarRating rating={form.rating} onRate={(r) => setForm({ ...form, rating: r })} />
                  </div>

                  {/* Feedback */}
                  <div>
                    <Label className="text-white text-sm mb-2 block">Experience & Feedback</Label>
                    <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                      placeholder="Share your experience with our service..." className="bg-black border-zinc-800 text-white h-24 resize-none" data-testid="gallery-input-desc" />
                  </div>

                  <Button onClick={handleSubmit} disabled={uploading} data-testid="gallery-submit-btn"
                    className="w-full h-12 rounded-full bg-amber-500 hover:bg-amber-400 text-black font-bold uppercase tracking-wide disabled:opacity-50">
                    {uploading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Uploading...</> : 'Submit for Review'}
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Divider */}
          <div className="border-t border-zinc-900 my-6" />

          {/* Approved Reviews Feed */}
          <div className="mb-8">
            <h2 className="text-white text-lg font-semibold mb-6 text-center" data-testid="approved-reviews-heading">Customer Reviews</h2>
          </div>

          {reviewsLoading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 text-amber-500 animate-spin" /></div>
          ) : !hasContent ? (
            <div className="text-center py-16">
              <Star className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
              <p className="text-zinc-500 text-sm">No reviews yet. Be the first to share your experience!</p>
            </div>
          ) : (
            <div className="space-y-5" data-testid="reviews-feed">
              {feed.map((item, idx) => {
                if (item.type === 'review') {
                  const rev = item.review;
                  return (
                    <div key={rev.id || idx} data-testid={`review-card-${rev.id || idx}`}
                      className="bg-zinc-950 border border-zinc-800/60 rounded-2xl p-5 hover:border-zinc-700/60 transition-colors">
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-white font-semibold text-sm">{rev.name}</p>
                        {(rev.date || rev.created_at) && (
                          <span className="text-zinc-600 text-[11px]">
                            {rev.date ? new Date(rev.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : new Date(rev.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </span>
                        )}
                      </div>
                      {rev.vehicle_info && <p className="text-zinc-500 text-[11px] mt-0.5">{rev.vehicle_info}</p>}
                      <div className="flex gap-0.5 my-2">
                        {[1, 2, 3, 4, 5].map((s) => (
                          <Star key={s} className={`w-3.5 h-3.5 ${s <= rev.rating ? 'text-amber-500 fill-amber-500' : 'text-zinc-800'}`} />
                        ))}
                      </div>
                      <p className="text-zinc-400 text-sm leading-relaxed mb-3">{rev.review}</p>
                    </div>
                  );
                }
                return null;
              })}
            </div>
          )}

        </div>
      </main>
      <Toaster position="top-right" />
    </div>
  );
};

export default SubmitReviewPage;
